import { Link } from "react-router-dom";
import { Users, Scale, ShieldAlert, Waves, Mountain, ListChecks, Clock, ArrowRight, Lock } from "lucide-react";
import { categoryName } from "@/data/simulations";

const ACCENT = "#4FA3D9";

const CATEGORY_ICON = {
  "everyday-life-skills": ListChecks,
  "social-relationships": Users,
  "decisions-pressure": Scale,
  "safety-emergencies": ShieldAlert,
  "change-uncertainty": Waves,
  "setbacks-adversity": Mountain,
};

// Preview/development builds may open the preview drawer of a coming-soon simulation for QA.
// Production builds keep the public Coming Soon behaviour (no Preview, no Practice).
const DEV_PREVIEW = process.env.NODE_ENV === "development";

export const SimulationCard = ({ sim, onPreview, resumable = false, onStartOver }) => {
  const available = sim.status === "available";
  const Icon = CATEGORY_ICON[sim.category] || ListChecks;
  const canPreview = !!sim.previewRoute && (available || DEV_PREVIEW);

  return (
    <div
      data-testid={`sim-card-${sim.slug}`}
      className={`group relative flex flex-col overflow-hidden rounded-2xl border bg-white/[0.02] transition-colors ${
        available ? "border-white/[0.08] hover:border-white/20" : "border-white/[0.05]"
      }`}
    >
      {/* visual band */}
      <div className="relative flex h-28 items-center justify-center overflow-hidden">
        {sim.image ? (
          <img src={sim.image} alt="" className={`h-full w-full object-cover ${available ? "" : "opacity-40 grayscale"}`} />
        ) : (
          <>
            <div
              className="absolute inset-0"
              style={{
                background: available
                  ? "linear-gradient(135deg, rgba(79,163,217,0.16), rgba(79,163,217,0.02))"
                  : "linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.01))",
              }}
            />
            <Icon
              size={30}
              strokeWidth={1.2}
              className="relative"
              style={{ color: available ? ACCENT : "rgba(255,255,255,0.28)" }}
            />
          </>
        )}
        {/* status badge */}
        <span
          data-testid={`sim-status-${sim.slug}`}
          className="absolute right-3 top-3 rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] backdrop-blur"
          style={
            available
              ? { borderColor: "rgba(79,163,217,0.45)", color: ACCENT, background: "rgba(6,19,26,0.82)" }
              : { borderColor: "rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.55)" }
          }
        >
          {available ? "Available Now" : "Coming Soon"}
        </span>
      </div>

      {/* content */}
      <div className="flex flex-1 flex-col p-5 sm:p-6">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/55">
          {categoryName(sim.category)}{sim.ageRange ? ` · ${sim.ageRange}` : ""}
        </p>
        <h3 className={`mt-2.5 font-display text-xl font-medium tracking-tight ${available ? "text-white" : "text-white/70"}`}>
          {sim.title}
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-white/50">{sim.description}</p>

        <div className="mt-auto flex flex-wrap items-center justify-between gap-x-3 gap-y-4 pt-6">
          {available ? (
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-white/55">
              <Clock size={13} />
              {sim.duration}
            </span>
          ) : (
            <span />
          )}

          {available ? (
            <div className="ml-auto flex flex-wrap items-center justify-end gap-x-4 gap-y-3">
              {canPreview && (
                <button
                  onClick={() => onPreview(sim)}
                  data-testid={`sim-preview-${sim.slug}`}
                  className="text-xs font-medium tracking-wide underline-offset-4 transition-colors hover:underline"
                  style={{ color: ACCENT }}
                >
                  Preview
                </button>
              )}
              {resumable && onStartOver && (
                <button
                  onClick={onStartOver}
                  data-testid={`sim-start-over-${sim.slug}`}
                  className="whitespace-nowrap text-xs font-medium tracking-wide text-white/55 underline-offset-4 transition-colors hover:text-white/85 hover:underline"
                >
                  Start Over
                </button>
              )}
              <Link
                to={sim.route}
                data-testid={`sim-practice-${sim.slug}`}
                className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] text-white transition-transform active:scale-[0.97]"
                style={{ background: ACCENT }}
              >
                {resumable ? "Resume" : "Practice"}
                <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5" />
              </Link>
            </div>
          ) : (
            <div className="ml-auto flex items-center gap-4">
              {canPreview && (
                <button
                  onClick={() => onPreview(sim)}
                  data-testid={`sim-preview-${sim.slug}`}
                  className="text-xs font-medium tracking-wide underline-offset-4 transition-colors hover:underline"
                  style={{ color: ACCENT }}
                >
                  Preview
                </button>
              )}
              <span
                data-testid={`sim-comingsoon-${sim.slug}`}
                aria-disabled="true"
                className="inline-flex cursor-default select-none items-center gap-2 rounded-full border border-white/12 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] text-white/50"
              >
                <Lock size={13} />
                Coming Soon
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SimulationCard;
